import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, BarChart, Filter, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { useFormProtection } from '../hooks/useFormProtection';
import HoneypotField from './HoneypotField';
import RecaptchaNotice from './RecaptchaNotice';

const ExecutiveDashboardSection = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    firm_name: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const navigate = useNavigate();
  const { honeypotProps, onInteraction, getProtectionPayload } = useFormProtection('pdf_download');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const protection = await getProtectionPayload();

      // The notification endpoint is the anti-abuse gate. If it rejects the
      // submission we must not hand out the PDF, so this error is no longer
      // swallowed.
      await axios.post('/api/send-pdf-notification', { ...formData, ...protection });
      console.log('Email notifications sent');

      // Create form and submit to trigger download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/download-pdf';
      form.target = '_blank';
      form.style.display = 'none';

      // Add form fields (including the protection payload)
      const downloadFields = { ...formData, ...protection };
      Object.keys(downloadFields).forEach(key => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = downloadFields[key] ?? '';
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      setSubmitMessage('Thanks! Your PDF is downloading now.');
      
      // Reset form after successful download
      setTimeout(() => {
        setFormData({
          full_name: '',
          email: '',
          firm_name: ''
        });
        setSubmitMessage('');
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitMessage(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'An error occurred. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-blue-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Executive Dashboard Sample (PDF)
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            See what your Practice CS data could look like — download a sample dashboard.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Features */}
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  Executive KPIs
                </h3>
                <p className="text-gray-600">
                  Revenue, margins, utilization, realization metrics at your fingertips
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BarChart className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  Drill-downs
                </h3>
                <p className="text-gray-600">
                  WIP, AR aging, partner & project performance breakdowns
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Filter className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  Smart Filters
                </h3>
                <p className="text-gray-600">
                  Filter by staff, seniority, project lead for detailed insights
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
            <div className="text-center mb-6">
              <FileText className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
                See How Your Firm Could Look in Practice Dashboard
              </h3>
              <p className="text-gray-600">
                Preview the insights your partners and managers could have at their fingertips.
              </p>
            </div>

            <form onSubmit={handleSubmit} onFocus={onInteraction} className="space-y-4">
              <HoneypotField {...honeypotProps} />
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors min-h-[50px]"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors min-h-[50px]"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label htmlFor="firm_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Firm Name *
                </label>
                <input
                  type="text"
                  id="firm_name"
                  name="firm_name"
                  value={formData.firm_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors min-h-[50px]"
                  placeholder="Enter your firm name"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 min-h-[48px] flex items-center justify-center"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  'Get My Dashboard Sample'
                )}
              </button>

              {submitMessage && (
                <div className={`mt-4 p-3 rounded-lg text-center ${
                  submitMessage.includes('Thanks!') 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {submitMessage}
                </div>
              )}
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              Free download • No spam • See what your dashboards could look like
            </p>

            <RecaptchaNotice className="mt-2" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExecutiveDashboardSection;